import { useState } from "react";

import { AuditLogTable, OfficesTable, RolloutDialog } from "../components/OfficeRollout";
import { PageHeader } from "../components/PageHeader";
import { SubStepShell } from "../components/StepShell";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../ui/card";
import { Input } from "../../../ui/input";
import { Label } from "../../../ui/label";
import { useOfficeRolloutActions, type OfficeMapping, type OfficePatch } from "../lib/api/office-mapping";

const DESCRIPTION =
  "Control which offices are live on Athena 2.0, when they went live, and keep a permanent record of every change.";

export function ManageOfficeRollout() {
  const { offices, auditLog, updateOffice } = useOfficeRolloutActions();

  const [actingAs, setActingAs] = useState("");
  const [actingError, setActingError] = useState<string | null>(null);
  const [target, setTarget] = useState<OfficeMapping | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  function onEdit(office: OfficeMapping) {
    setTarget(office);
    setDialogOpen(true);
  }

  function onSave(officeName: string, patch: OfficePatch) {
    if (!actingAs.trim()) {
      setActingError("Enter your name before saving a change.");
      return;
    }
    setActingError(null);
    // The save is a real request now, so a rejection has to land somewhere the
    // user can see — silently swallowing it would leave the dialog closed and
    // the table unchanged, which reads as "saved".
    Promise.resolve(updateOffice(officeName, patch, actingAs.trim())).catch(
      (e: unknown) => setActingError((e as Error).message),
    );
  }

  return (
    <SubStepShell subStepId="manage-office-rollout" backLabel="Underwriting Health">
      <PageHeader title="Manage Office Rollout" subtitle={DESCRIPTION} />

      <div className="max-w-xs space-y-2">
        <Label htmlFor="acting-as" className="text-xs uppercase tracking-wider text-muted-foreground">
          Acting as
        </Label>
        <Input
          id="acting-as"
          placeholder="Your name"
          value={actingAs}
          onChange={(e) => {
            setActingAs(e.target.value);
            if (e.target.value.trim()) setActingError(null);
          }}
        />
        {actingError ? <p className="text-xs text-destructive">{actingError}</p> : null}
        <p className="text-xs text-muted-foreground">
          Session-only — recorded as the author of every change you save below.
        </p>
      </div>

      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-base font-semibold">Offices</CardTitle>
          <CardDescription>
            Office numbers come from the LeaseWorks mapping and are read-only here. Everything else
            is editable per office.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <OfficesTable rows={offices} onEdit={onEdit} />
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardHeader>
          <CardTitle className="text-base font-semibold">Audit Log</CardTitle>
          <CardDescription>
            Append-only record of every rollout change, newest first. Nothing is ever removed.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <AuditLogTable entries={auditLog} />
        </CardContent>
      </Card>

      <RolloutDialog
        office={target}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        actingAs={actingAs}
        onSave={onSave}
      />
    </SubStepShell>
  );
}
